import type { ThunkAction } from '../../types/app.types';
import { CMS_META_TAG_ENABLED } from '../../config';

/*************************************************************************
 * Types
 **************************************************************************/
export type MetaTagResponse = {
  noindex: boolean;
  title: string;
  canonical: string;
  description: string;
  route: string;
};
export type ResponsiveImage = {
  desktop: string;
  tablet: string;
  mobile: string;
};
export type DepartmentHero = {
  backgroundImage: ResponsiveImage;
  title: string;
  description: any;
};
export type DepartmentTextBlock = {
  title: string;
  description: any;
};
export type DepartmentFeature = {
  title: string;
  image: string;
  featureNames: Array<string>;
};
export type DepartmentFeatures = {
  title: string;
  features: Array<DepartmentFeature>;
};
export type DepartmentLocation = {
  addressLine1: string;
  addressLine2: string;
  image: string;
  name: string;
};
export type DepartmentLocations = {
  title: string;
  locations: Array<DepartmentLocation>;
};
export type Leader = {
  name: string;
  title: string;
  subTitle: string;
  profilePic: string;
  previously: Array<string>;
};
export type Leadership = {
  title: string;
  leaders: Array<Leader>;
};
export type Team = {
  title: string;
  images: Array<string>;
};
export type Testimonial = {
  author: string;
  authorPicture: string;
  content: any;
};
export type DepartmentsResponse = {
  name: string;
  slug: string;
  departmentLocations: DepartmentLocations;
  leadership: Leadership;
  meetTheTeam: Team;
  testimonial: Testimonial;
  hero: DepartmentHero;
  departmentFeatures: DepartmentFeatures;
  textBlock: DepartmentTextBlock;
};
export type DrugInfoResponse = {
  slug: string;
  results: Array<Record<string, any>>;
  drugInfoRewrite: Record<string, any>;
};
export type MetaTagResponses = Record<string, MetaTagResponse>;
export type DepartmentsResponses = Record<string, DepartmentsResponse>;
export type FetchMetaTag = {
  type: 'cms/FETCH_META_TAG';
  res: MetaTagResponse;
  key: string;
};
export type FetchDepartments = {
  type: 'cms/FETCH_DEPARTMENT';
  res: DepartmentsResponse;
  key: string;
};
export type DrugInfoResponses = Record<string, DrugInfoResponse>;
export type FetchDrugInfo = {
  type: 'cms/FETCH_DRUG_INFO';
  res: DrugInfoResponse;
};
export type FetchPreviewBlogDetails = {
  type: 'cms/FETCH_PREVIEW_BLOG_DETAILS';
  res: Record<string, any>;
};
// Actions
export type CMSAction = FetchMetaTag | FetchDepartments | FetchDrugInfo | FetchPreviewBlogDetails;
export type CMSState = {
  metaTag: MetaTagResponses;
  departments: DepartmentsResponses;
  drugInfo: DrugInfoResponses;
  blogPreviewDetails: Record<string, any>;
};

/*************************************************************************
 * Initial State
 **************************************************************************/
export const initialState: CMSState = {
  metaTag: {},
  departments: {},
  drugInfo: {},
  blogPreviewDetails: {},
};

export function fetchMetaTag(route: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    if (CMS_META_TAG_ENABLED) {
      return blinkAPIClient
        .get(`/cms/meta_tag?route=${route}`)
        .then((res) => res.data)
        .then((data) => {
          if (data.result) {
            return dispatch({
              type: 'cms/FETCH_META_TAG',
              res: data.result,
              key: route,
            });
          }
        })
        .catch((error) => {
          console.error(error);
        });
    }

    return Promise.resolve();
  };
}

export function fetchDepartments(department: string): ThunkAction<Promise<any>> {
  return function (dispatch, getState, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`cms/web/departments/${department}`)
      .then((res) => res.data)
      .then((data) => {
        if (data.result) {
          return dispatch({
            type: 'cms/FETCH_DEPARTMENT',
            res: data.result,
            key: department,
          });
        }
      })
      .catch(() => {
        // TODO: Handle error
      });
  };
}

export function fetchDrugInfo(slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, _, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/cms/drug_info/${slug}`)
      .then((res) => res.data)
      .then((data) =>
        dispatch({
          type: 'cms/FETCH_DRUG_INFO',
          res: data,
        })
      )
      .catch(() => {
        // TODO: Handle error
      });
  };
}

export function fetchPreviewBlogDetails(slug: string): ThunkAction<Promise<any>> {
  return function (dispatch, _, { blinkAPIClient }): Promise<any> {
    return blinkAPIClient
      .get(`/cms/blogs/${slug}/preview`)
      .then((res) => res.data)
      .then((data) =>
        dispatch({
          type: 'cms/FETCH_PREVIEW_BLOG_DETAILS',
          res: data.result,
        })
      )
      .catch(() => {
        // TODO: Handle error
      });
  };
}

/*************************************************************************
 * Reducer
 **************************************************************************/

export default function cmsReducer(state: CMSState = { ...initialState }, action: CMSAction): CMSState {
  switch (action.type) {
    case 'cms/FETCH_META_TAG': {
      return { ...state, metaTag: { ...state.metaTag, [action.key]: action.res } };
    }

    case 'cms/FETCH_DEPARTMENT': {
      return { ...state, departments: { ...state.departments, [action.key]: action.res } };
    }

    case 'cms/FETCH_DRUG_INFO': {
      return { ...state, drugInfo: { ...state.drugInfo, [action.res.slug]: action.res } };
    }

    case 'cms/FETCH_PREVIEW_BLOG_DETAILS': {
      return { ...state, blogPreviewDetails: { ...state.blogPreviewDetails, [action.res.slug]: action.res } };
    }

    default:
      return state;
  }
}
